import React, { useState, useEffect } from "react";
import { Check, Minus } from "lucide-react";
import { MarketingShell, Container, Reveal, Eyebrow, TrialButton, useSeo, FONT, JsonLd, SITE } from "./Marketing";
import { track } from "../../lib/analytics";

const PLANS = [
  { id: "starter", name: "Starter", monthly: 89.99, yearly: 899, tagline: "Get your branded designer live and capturing leads.", popular: false,
    highlights: ["Branded turf designer", "Embed on your website", "Unlimited manual designs", "Lead capture & email alerts"] },
  { id: "ai_designer", name: "AI Designer", monthly: 149.99, yearly: 1499, tagline: "Add AI concept generation to wow every visitor.", popular: false,
    highlights: ["Everything in Starter", "AI turf generation", "150 AI generations / month", "Multi-currency pricing"] },
  { id: "ai_pro", name: "AI Pro", monthly: 249.99, yearly: 2499, tagline: "For busy installers who live on inbound leads.", popular: true,
    highlights: ["Everything in AI Designer", "500 AI generations / month", "Conversion analytics", "Priority support"] },
  { id: "white_label", name: "White Label", monthly: 399.99, yearly: 3999, tagline: "Make it 100% yours — no trace of us.", popular: false,
    highlights: ["Everything in AI Pro", "Unlimited AI generations", "Remove our branding", "Custom domain (coming soon)"] },
];

const COMPARE = [
  ["Branded turf designer", true, true, true, true],
  ["Embed on your website", true, true, true, true],
  ["Lead capture & notifications", true, true, true, true],
  ["Manual designs", "Unlimited", "Unlimited", "Unlimited", "Unlimited"],
  ["AI turf generation", false, true, true, true],
  ["AI monthly allowance", "—", "150", "500", "Unlimited"],
  ["Multi-currency pricing", false, true, true, true],
  ["Conversion analytics", false, false, true, true],
  ["Remove our branding", false, false, false, true],
  ["Priority support", false, false, true, true],
];

const money = (v) => "£" + Number(v).toLocaleString("en-GB", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

export default function Pricing() {
  useSeo("Pricing — AI Turf Designer", "Simple pricing for a powerful sales engine. Plans from £89.99/month with a 14-day free trial. Starter, AI Designer, AI Pro and White Label.");
  useEffect(() => { track("pricing_view", { page: "pricing" }); }, []);
  const [yearly, setYearly] = useState(false);

  const cell = (v) => v === true ? <Check size={18} className="text-[#00D26A] mx-auto" />
    : v === false ? <Minus size={16} className="text-zinc-600 mx-auto" />
    : <span className="text-sm text-zinc-300">{v}</span>;

  return (
    <MarketingShell>
      <JsonLd id="ld-pricing" data={{
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Turf Designer",
        "description": SITE.description,
        "brand": { "@type": "Brand", "name": "AI Turf Designer" },
        "offers": PLANS.map((p) => ({ "@type": "Offer", "name": p.name, "price": String(p.monthly), "priceCurrency": "GBP", "url": SITE.url + "/pricing", "availability": "https://schema.org/InStock" })),
      }} />
      <section className="pt-20 pb-10 border-b border-white/10" data-testid="pricing-hero">
        <Container className="text-center">
          <Reveal><Eyebrow className="justify-center">Pricing</Eyebrow></Reveal>
          <Reveal delay={0.05}><h1 className="mt-4 text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none" style={{ fontFamily: FONT.display }}>Simple pricing for a powerful sales engine</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-zinc-400 text-lg max-w-2xl mx-auto">Every plan includes a 14-day free trial. No card needed to sign up.</p></Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 inline-flex items-center gap-3 bg-[#161616] border border-white/10 rounded-full p-1.5" data-testid="billing-toggle">
              <button onClick={() => setYearly(false)} data-testid="toggle-monthly" className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!yearly ? "bg-[#00D26A] text-black" : "text-zinc-400"}`}>Monthly</button>
              <button onClick={() => setYearly(true)} data-testid="toggle-yearly" className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${yearly ? "bg-[#00D26A] text-black" : "text-zinc-400"}`}>Yearly <span className="text-[#00D26A] ml-1">Save ~17%</span></button>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className="py-16">
        <Container>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PLANS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div data-testid={`plan-${p.id}`} className={`relative h-full flex flex-col rounded-2xl p-7 bg-[#161616] border ${p.popular ? "border-[#00D26A] border-t-2 shadow-[0_0_50px_rgba(0,210,106,0.12)]" : "border-white/8"}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00D26A] text-black text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">Most Popular</span>}
                  <h3 className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: FONT.display }}>{p.name}</h3>
                  <p className="mt-2 text-sm text-zinc-400 min-h-[40px]">{p.tagline}</p>
                  <div className="mt-5">
                    <span className="text-4xl font-black text-white" style={{ fontFamily: FONT.display }}>{money(yearly ? p.yearly : p.monthly)}</span>
                    <span className="text-zinc-500 text-sm ml-1">/{yearly ? "year" : "month"}</span>
                  </div>
                  {yearly && <div className="mt-1 text-xs text-[#00D26A]">≈ {money(p.yearly / 12)}/mo billed annually</div>}
                  <TrialButton source={`pricing-${p.id}`} testid={`plan-${p.id}-cta`} variant={p.popular ? "primary" : "secondary"} className="w-full mt-6">Start Free Trial</TrialButton>
                  <ul className="mt-7 space-y-3">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-zinc-300"><Check size={17} className="text-[#00D26A] shrink-0 mt-0.5" />{h}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 border-t border-white/10" data-testid="comparison-table">
        <Container>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-8" style={{ fontFamily: FONT.display }}>Compare every plan</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left min-w-[720px]">
              <thead>
                <tr className="bg-[#161616] border-b border-white/10">
                  <th className="p-4 text-xs uppercase tracking-widest text-zinc-500 font-semibold">Feature</th>
                  {PLANS.map((p) => <th key={p.id} className="p-4 text-center text-sm font-bold uppercase text-white" style={{ fontFamily: FONT.display }}>{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={row[0]} className={`border-b border-white/5 ${i % 2 ? "bg-[#0d0d0d]" : ""}`}>
                    <td className="p-4 text-sm text-zinc-300">{row[0]}</td>
                    {row.slice(1).map((v, j) => <td key={j} className="p-4 text-center">{cell(v)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>
    </MarketingShell>
  );
}
